import { Box, IconButton, styled } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import theme from '@shared/themes/darkTheme';
import { CarouselProps } from '@features/dashboard/freemium/types/types';
import { useCarousel } from '../hooks/useCarousel';
import { CARD_WIDTH_VALUES } from '../constants';
import { useState } from 'react';

const CarouselNavButton = styled(IconButton)(() => ({
  backgroundColor: theme.palette.background.lighter,
  backdropFilter: 'blur(10px)',
  border: `1px solid ${theme.palette.border}`,
  boxShadow: `0 4px 12px ${theme.palette.background.boxShadow}`,
  '&:hover': {
    backgroundColor: theme.palette.background.lighter,
    boxShadow: `0 6px 16px ${theme.palette.background.boxShadow}`,
  },
  '&:disabled': {
    opacity: 0.3,
    cursor: 'not-allowed',
  },
  transition: 'all 0.2s ease-in-out',
  '& svg': {
    color: theme.palette.text.primary,
    fontSize: '20px',
    fontWeight: 'bold',
  },
}));

export const Carousel = <T,>({
  items,
  renderItem,
  itemHeight = '235px',
  showNavigation = true,
  gap = 16,
  onItemClick,
  className,
  sx = {},
  linearGradient = false,
  useAnimation = true,
}: CarouselProps<T>) => {
  const { carouselRef, scrollContainerRef, canScrollLeft, canScrollRight, dynamicGap, currentGap, handlePrevious, handleNext } = useCarousel<T>({
    gap,
    items,
    useAnimation,
  });

  const [isHovered, setIsHovered] = useState(false);

  return (
    <Box
      ref={carouselRef}
      className={className}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: linearGradient ? 'linear-gradient(to right, transparent, black, transparent)' : 'transparent',
        padding: '5px',
        position: 'relative',
        ...sx,
      }}
    >
      <Box
        ref={scrollContainerRef}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          overflowX: dynamicGap !== undefined ? 'hidden' : 'auto',
          overflowY: 'hidden',
          minHeight: itemHeight,
          alignItems: 'flex-start',
          pt: '1%',
          pb: '1%',
          gap: currentGap,
          scrollbarColor: `${theme.palette.border} transparent`,
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme.palette.border,
            borderRadius: '3px',
            '&:hover': {
              background: theme.palette.text.secondary,
            },
          },
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {items.map((item: T, index: number) => {
          return (
            <Box
              key={`${JSON.stringify(item)}-${index}`}
              sx={{
                flex: `0 0 ${CARD_WIDTH_VALUES}`,
                maxWidth: CARD_WIDTH_VALUES,
                minWidth: CARD_WIDTH_VALUES,
                width: CARD_WIDTH_VALUES,
                height: itemHeight,
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
              }}
              onClick={() => onItemClick?.(item, index)}
            >
              {renderItem(item, index)}
            </Box>
          );
        })}
      </Box>

      {!dynamicGap && showNavigation && (
        <>
          <CarouselNavButton
            onClick={handlePrevious}
            disabled={!canScrollLeft}
            sx={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              opacity: isHovered && canScrollLeft ? 1 : 0,
              pointerEvents: isHovered && canScrollLeft ? 'auto' : 'none',
              transition: 'opacity 0.2s ease-in-out',
            }}
          >
            <ChevronLeft />
          </CarouselNavButton>
          <CarouselNavButton
            onClick={handleNext}
            disabled={!canScrollRight}
            sx={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              opacity: isHovered && canScrollRight ? 1 : 0,
              pointerEvents: isHovered && canScrollRight ? 'auto' : 'none',
              transition: 'opacity 0.2s ease-in-out',
            }}
          >
            <ChevronRight />
          </CarouselNavButton>
        </>
      )}
    </Box>
  );
};
